import apiClient from '../utils/axiosConfig.ts';

export interface ProductCustomerPair {
  product_id: number;
  customer_id: number;
}

const devToolsService = {
  getDatabaseStatus: async () => {
    const response = await apiClient.get('/dev-tools/database-status');
    return response.data;
  },

  // Strumento A: associazioni prodotto-cliente mancanti (stock con cliente ma senza product_customers)
  getMissingCustomerAssociations: async () => {
    const response = await apiClient.get('/dev-tools/missing-customer-associations');
    return response.data;
  },
  createMissingCustomerAssociations: async (pairs: ProductCustomerPair[]) => {
    const response = await apiClient.post('/dev-tools/missing-customer-associations/create', { pairs });
    return response.data;
  },

  // Strumento B: prodotti in stock senza cliente e senza alcuna associazione product_customers
  getStockWithoutCustomer: async () => {
    const response = await apiClient.get('/dev-tools/stock-without-customer');
    return response.data;
  },
  associateStockWithoutCustomer: async (pairs: ProductCustomerPair[]) => {
    const response = await apiClient.post('/dev-tools/stock-without-customer/associate', { pairs });
    return response.data;
  },

  // Strumento C: Rollback Evasione Ordine Cliente (solo admin, /api/fulfillment-rollback)
  getFulfilledOrders: async (search?: string) => {
    const response = await apiClient.get('/fulfillment-rollback/orders', { params: { search: search || undefined } });
    return response.data;
  },
  previewFulfillmentRollback: async (orderId: number, targetStatusId?: number) => {
    const response = await apiClient.get(`/fulfillment-rollback/${orderId}/preview`, {
      params: { target_status_id: targetStatusId }
    });
    return response.data;
  },
  applyFulfillmentRollback: async (orderId: number, payload: { notes?: string; target_status_id?: number }) => {
    const response = await apiClient.post(`/fulfillment-rollback/${orderId}`, payload);
    return response.data;
  },

  // Strumento D: Backup Database (solo admin, /api/db-backup). Il file resta sul server.
  getBackupStatus: async (): Promise<DbBackupStatus> => {
    const response = await apiClient.get('/db-backup/status');
    return response.data;
  },
  runBackup: async (): Promise<DbBackupStatus & { message: string }> => {
    const response = await apiClient.post('/db-backup/run');
    return response.data;
  },

  // Strumento E: Tentativi di Accesso (solo admin, /api/login-attempts): registro dei login
  getLoginAttempts: async (params: {
    date_from?: string; date_to?: string; username?: string; ip_address?: string;
    outcome?: string; limit?: number; offset?: number;
  }): Promise<LoginAttemptsResponse> => {
    const response = await apiClient.get('/login-attempts', { params });
    return response.data;
  },
  getActiveLoginBlocks: async (): Promise<{ blocks: ActiveLoginBlock[] }> => {
    const response = await apiClient.get('/login-attempts/active-blocks');
    return response.data;
  }
};

export interface LoginAttempt {
  id: number;
  attempted_at: string;
  username: string;
  ip_address: string | null;
  outcome: 'success' | 'failed' | 'blocked';
  block_reason: 'USER_IP' | 'IP' | 'USER' | null;
  user_exists: boolean;
  user_agent: string | null;
}

export interface LoginAttemptsResponse {
  total: number;
  items: LoginAttempt[];
  summary: {
    success: number;
    failed: number;
    blocked: number;
    top_ips: { ip_address: string | null; failed: number; blocked: number; usernames: number }[];
  };
  rules: { window_minutes: number; user_ip_max_failures: number; ip_max_failures: number; user_max_failures: number };
}

export interface ActiveLoginBlock {
  reason: 'USER_IP' | 'IP' | 'USER';
  username: string | null;
  ip_address: string | null;
  until: string;
}

export interface DbBackupRun {
  state: 'running' | 'success' | 'failed';
  started_at: string;
  finished_at: string | null;
  user: string;
  database: string;
  file_name: string;
  size_bytes: number | null;
  sql_size_bytes?: number | null;
  directory: string;
  error: string | null;
}

export interface DbBackupStatus {
  running: boolean;
  last: DbBackupRun | null;
  directory: string;
  backups: { file_name: string; size_bytes: number; created_at: string }[];
}

export default devToolsService;
